import { supabase } from '@/integrations/supabase/client';

export type IntegrationName = 'google_calendar' | 'calendly' | 'stripe' | 'email' | 'system_health';
export type IntegrationStatus = 'success' | 'warning' | 'error' | 'not_configured' | 'not_checked';
export type LogStatus = 'success' | 'warning' | 'error';

export interface IntegrationLog {
  id: string;
  integration_name: IntegrationName;
  status: LogStatus;
  message: string;
  details: Record<string, unknown> | null;
  run_by_user_id: string | null;
  created_at: string;
}

export interface IntegrationStatusRecord {
  id: string;
  integration_name: IntegrationName;
  last_status: IntegrationStatus;
  last_message: string;
  last_checked_at: string | null;
  updated_at: string;
}

export interface SystemHealthStatus {
  overall: 'healthy' | 'degraded' | 'down';
  integrations: {
    google_calendar: IntegrationStatusRecord | null;
    calendly: IntegrationStatusRecord | null;
    stripe: IntegrationStatusRecord | null;
    email: IntegrationStatusRecord | null;
  };
  lastChecked: string | null;
}

// Fetch all integration status records
export async function getIntegrationStatuses(): Promise<IntegrationStatusRecord[]> {
  const { data, error } = await supabase
    .from('integration_status')
    .select('*')
    .order('integration_name');

  if (error) {
    console.error('Error fetching integration statuses:', error);
    throw error;
  }

  return data as IntegrationStatusRecord[];
}

// Fetch a single integration status
export async function getIntegrationStatus(integrationName: IntegrationName): Promise<IntegrationStatusRecord | null> {
  const { data, error } = await supabase
    .from('integration_status')
    .select('*')
    .eq('integration_name', integrationName)
    .maybeSingle();

  if (error) {
    console.error('Error fetching integration status:', error);
    throw error;
  }

  return data as IntegrationStatusRecord | null;
}

// Update integration status after a test
export async function updateIntegrationStatus(
  integrationName: IntegrationName,
  status: IntegrationStatus,
  message: string
): Promise<void> {
  const { error } = await supabase
    .from('integration_status')
    .update({
      last_status: status,
      last_message: message,
      last_checked_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('integration_name', integrationName);

  if (error) {
    console.error('Error updating integration status:', error);
    throw error;
  }
}

// Log an integration test result
export async function logIntegrationTest(
  integrationName: IntegrationName,
  status: LogStatus,
  message: string,
  details?: Record<string, unknown>,
  userId?: string
): Promise<void> {
  const { error } = await supabase
    .from('integration_logs')
    .insert([{
      integration_name: integrationName,
      status,
      message,
      details: details || null,
      run_by_user_id: userId || null,
    }] as any);

  if (error) {
    console.error('Error logging integration test:', error);
    throw error;
  }
}

// Fetch integration logs with pagination and filtering
export async function getIntegrationLogs(params: {
  integrationName?: IntegrationName | 'all';
  page?: number;
  pageSize?: number;
}): Promise<{ logs: IntegrationLog[]; total: number }> {
  const { integrationName = 'all', page = 1, pageSize = 20 } = params;
  const offset = (page - 1) * pageSize;

  let query = supabase
    .from('integration_logs')
    .select('*', { count: 'exact' });

  if (integrationName !== 'all') {
    query = query.eq('integration_name', integrationName);
  }

  const { data, error, count } = await query
    .order('created_at', { ascending: false })
    .range(offset, offset + pageSize - 1);

  if (error) {
    console.error('Error fetching integration logs:', error);
    throw error;
  }

  return {
    logs: data as IntegrationLog[],
    total: count || 0,
  };
}

// Calculate overall system health
export function calculateSystemHealth(statuses: IntegrationStatusRecord[]): SystemHealthStatus['overall'] {
  const criticalIntegrations = ['google_calendar', 'stripe', 'email'];
  const relevantStatuses = statuses.filter(s => criticalIntegrations.includes(s.integration_name));

  const hasError = relevantStatuses.some(s => s.last_status === 'error');
  const hasWarningOrNotConfigured = relevantStatuses.some(
    s => s.last_status === 'warning' || s.last_status === 'not_configured' || s.last_status === 'not_checked'
  );

  if (hasError) return 'down';
  if (hasWarningOrNotConfigured) return 'degraded';
  return 'healthy';
}

// Get full system health status
export async function getSystemHealth(): Promise<SystemHealthStatus> {
  const statuses = await getIntegrationStatuses();

  const googleCalendar = statuses.find(s => s.integration_name === 'google_calendar') || null;
  const calendly = statuses.find(s => s.integration_name === 'calendly') || null;
  const stripe = statuses.find(s => s.integration_name === 'stripe') || null;
  const email = statuses.find(s => s.integration_name === 'email') || null;

  const lastChecked = statuses
    .filter(s => s.last_checked_at)
    .sort((a, b) => new Date(b.last_checked_at!).getTime() - new Date(a.last_checked_at!).getTime())[0]
    ?.last_checked_at || null;

  return {
    overall: calculateSystemHealth(statuses),
    integrations: {
      google_calendar: googleCalendar,
      calendly,
      stripe,
      email,
    },
    lastChecked,
  };
}

// Log system health check result
export async function logSystemHealthCheck(
  overall: SystemHealthStatus['overall'],
  message: string,
  details?: Record<string, unknown>,
  userId?: string
): Promise<void> {
  const status: LogStatus = overall === 'healthy' ? 'success' : overall === 'degraded' ? 'warning' : 'error';

  await logIntegrationTest('system_health', status, message, details, userId);
}
